import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/utils';
import { Plus, Package, Clock, CreditCard, CheckCircle, XCircle, Loader2, ShieldCheck, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
    pending_review: { label: 'Pending Review', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
    priced: { label: 'Price Set - Ready to Pay', color: 'bg-blue-500/20 text-blue-400', icon: CreditCard },
    pending_payment: { label: 'Payment Pending', color: 'bg-orange-500/20 text-orange-400', icon: Loader2 },
    paid: { label: 'Paid - Awaiting Production', color: 'bg-emerald-500/20 text-emerald-400', icon: Clock },
    in_progress: { label: 'In Production', color: 'bg-purple-500/20 text-purple-400', icon: Package },
    completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: XCircle },
};

function PaymentOverlay({ overlay, onClose }) {
    if (!overlay.active) return null;
    return (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="payment-overlay">
            <div className="bg-[#121620] border border-white/10 rounded-2xl p-6 sm:p-8 max-w-md w-full text-center">
                {overlay.status === 'loading' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                            <Loader2 className="w-8 h-8 text-gold animate-spin" />
                        </div>
                        <h3 className="font-outfit text-xl font-bold text-white mb-2">Preparing Payment</h3>
                        <p className="text-muted-foreground text-sm">Opening secure checkout window...</p>
                    </>
                )}
                {overlay.status === 'waiting' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
                            <ShieldCheck className="w-8 h-8 text-gold" />
                        </div>
                        <h3 className="font-outfit text-xl font-bold text-white mb-2">Complete Your Payment</h3>
                        <p className="text-muted-foreground text-sm mb-1">A secure payment window has opened.</p>
                        {overlay.title && (
                            <p className="text-muted-foreground text-sm mb-1">
                                <span className="text-white font-medium">{overlay.title}</span>
                            </p>
                        )}
                        {overlay.amount > 0 && (
                            <p className="text-gold font-bold text-lg mb-4">{formatCurrency(overlay.amount)}</p>
                        )}
                        <div className="flex items-center justify-center gap-2 text-xs text-white/30 mb-6">
                            <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                            Waiting for payment confirmation...
                        </div>
                        <Button variant="ghost" onClick={onClose} className="text-white/50 hover:text-white text-sm" data-testid="cancel-payment-btn">
                            Cancel Payment
                        </Button>
                    </>
                )}
                {overlay.status === 'success' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                            <Check className="w-8 h-8 text-emerald-400" />
                        </div>
                        <h3 className="font-outfit text-xl font-bold text-white mb-2">Payment Successful!</h3>
                        <p className="text-muted-foreground text-sm">Your branded content request is now in production.</p>
                    </>
                )}
            </div>
        </div>
    );
}

export default function OperatorContentRequests() {
    const { user } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [cities, setCities] = useState([]);
    const [form, setForm] = useState({ title: '', description: '', city_id: '', notes: '' });
    const [submitting, setSubmitting] = useState(false);
    const [overlay, setOverlay] = useState({ active: false });
    const popupRef = useRef(null);
    const pollRef = useRef(null);

    const fetchRequests = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/operator/content-requests`, { credentials: 'include' });
            if (res.ok) setRequests(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchRequests();
        fetch(`${API}/api/public/cities`).then(r => r.json()).then(setCities).catch(() => {});
    }, [fetchRequests]);

    // Check for return from Stripe (popup redirect)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');
        if (!sessionId) return;

        setOverlay({ active: true, status: 'waiting', title: 'Branded Content', amount: 0 });
        let attempts = 0;
        const poll = setInterval(async () => {
            attempts++;
            if (attempts > 10) { clearInterval(poll); setOverlay({ active: false }); return; }
            try {
                const res = await fetch(`${API}/api/payments/status/${sessionId}`, { credentials: 'include' });
                const data = await res.json();
                if (data.payment_status === 'paid') {
                    clearInterval(poll);
                    setOverlay({ active: true, status: 'success' });
                    window.history.replaceState({}, '', window.location.pathname);
                    fetchRequests();
                    setTimeout(() => setOverlay({ active: false }), 3000);
                }
            } catch (e) { console.error(e); }
        }, 2000);
        return () => clearInterval(poll);
    }, [fetchRequests]);

    useEffect(() => {
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/api/operator/content-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form)
            });
            if (res.ok) {
                setForm({ title: '', description: '', city_id: '', notes: '' });
                setShowForm(false);
                fetchRequests();
            }
        } catch (e) { console.error(e); }
        finally { setSubmitting(false); }
    };

    const handlePayment = async (req) => {
        setOverlay({ active: true, status: 'loading', title: req.title, amount: req.price });
        try {
            const res = await fetch(`${API}/api/operator/content-requests/${req.id}/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ origin_url: window.location.origin })
            });
            const data = await res.json();
            if (!data.url) { setOverlay({ active: false }); return; }

            const w = 520, h = 720;
            const left = (window.screen.width - w) / 2;
            const top = (window.screen.height - h) / 2;
            const popup = window.open(data.url, 'voxoora-checkout', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
            popupRef.current = popup;

            setOverlay({ active: true, status: 'waiting', title: req.title, amount: req.price, session_id: data.session_id });

            pollRef.current = setInterval(async () => {
                if (popup && popup.closed) {
                    clearInterval(pollRef.current);
                    try {
                        const statusRes = await fetch(`${API}/api/payments/status/${data.session_id}`, { credentials: 'include' });
                        const statusData = await statusRes.json();
                        if (statusData.payment_status === 'paid') {
                            setOverlay({ active: true, status: 'success', title: req.title });
                            fetchRequests();
                            setTimeout(() => setOverlay({ active: false }), 3000);
                        } else {
                            setOverlay({ active: false });
                        }
                    } catch { setOverlay({ active: false }); }
                    return;
                }
                try {
                    const statusRes = await fetch(`${API}/api/payments/status/${data.session_id}`, { credentials: 'include' });
                    const statusData = await statusRes.json();
                    if (statusData.payment_status === 'paid') {
                        clearInterval(pollRef.current);
                        if (popup && !popup.closed) popup.close();
                        setOverlay({ active: true, status: 'success', title: req.title });
                        fetchRequests();
                        setTimeout(() => setOverlay({ active: false }), 3000);
                    }
                } catch { /* keep polling */ }
            }, 2500);

            if (!popup || popup.closed) {
                clearInterval(pollRef.current);
                setOverlay({ active: false });
                alert('Popup was blocked. Please allow popups for this site and try again.');
            }
        } catch (e) {
            console.error(e);
            setOverlay({ active: false });
        }
    };

    const cancelPayment = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
        setOverlay({ active: false });
    };

    return (
        <DashboardLayout type="operator">
            <PaymentOverlay overlay={overlay} onClose={cancelPayment} />

            <div className="space-y-6" data-testid="content-requests-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white" data-testid="content-requests-title">Branded Content</h1>
                        <p className="text-sm text-white/50 mt-1">Request custom audio content for your brand</p>
                    </div>
                    <Button onClick={() => setShowForm(!showForm)} data-testid="new-request-btn" className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold">
                        <Plus className="w-4 h-4 mr-2" /> New Request
                    </Button>
                </div>

                {showForm && (
                    <div className="bg-[#12151C] border border-white/10 rounded-lg p-6" data-testid="content-request-form">
                        <h3 className="text-lg font-semibold text-white mb-4">Request New Branded Content</h3>
                        <p className="text-sm text-white/50 mb-4">Describe the audio content you need. Our admin team will review and provide a quote. Once paid, content will be produced in 3-6 weeks with 4 base languages (EN, FR, ES, DE).</p>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/70">Content Title *</Label>
                                    <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Vatican City Audio Tour" className="bg-[#0A0D14] border-white/10 text-white" data-testid="content-title-input" required />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">City</Label>
                                    <select value={form.city_id} onChange={e => setForm({ ...form, city_id: e.target.value })} className="w-full h-10 rounded-md bg-[#0A0D14] border border-white/10 text-white px-3 text-sm" data-testid="content-city-select">
                                        <option value="">Select city (optional)</option>
                                        {cities.map(c => <option key={c.id} value={c.id}>{c.name}, {c.country}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/70">Description</Label>
                                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the content you need: locations, stories, themes..." rows={3} className="w-full rounded-md bg-[#0A0D14] border border-white/10 text-white px-3 py-2 text-sm" data-testid="content-description-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/70">Additional Notes</Label>
                                <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any special requirements..." className="bg-[#0A0D14] border-white/10 text-white" />
                            </div>
                            <div className="flex gap-3">
                                <Button type="submit" disabled={submitting} className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold" data-testid="submit-request-btn">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Submit Request
                                </Button>
                                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-white/20 text-white/70">Cancel</Button>
                            </div>
                        </form>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-12 text-white/40"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading...</div>
                ) : requests.length === 0 ? (
                    <div className="text-center py-16 bg-[#12151C] rounded-lg border border-white/5">
                        <Package className="w-12 h-12 text-white/20 mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-white/60">No content requests yet</h3>
                        <p className="text-sm text-white/30 mt-1">Request custom branded audio content to get started</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {requests.map(req => {
                            const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending_review;
                            const Icon = cfg.icon;
                            return (
                                <div key={req.id} className="bg-[#12151C] border border-white/10 rounded-lg p-5" data-testid={`content-request-${req.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                <h3 className="text-lg font-semibold text-white">{req.title}</h3>
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`} data-testid={`status-badge-${req.id}`}>
                                                    <Icon className="w-3 h-3 inline mr-1" />{cfg.label}
                                                </span>
                                            </div>
                                            {req.description && <p className="text-sm text-white/50 mb-2">{req.description}</p>}
                                            {req.city_name && <p className="text-xs text-white/30">City: {req.city_name}</p>}
                                            {req.admin_notes && (
                                                <div className="mt-3 bg-[#0A0D14] rounded-md p-3 border border-white/5">
                                                    <p className="text-xs text-white/40 mb-1">Admin Notes:</p>
                                                    <p className="text-sm text-white/70">{req.admin_notes}</p>
                                                </div>
                                            )}
                                            <p className="text-xs text-white/20 mt-2">Requested: {new Date(req.created_at).toLocaleDateString()}</p>
                                        </div>
                                        <div className="text-right ml-4 flex flex-col items-end gap-2">
                                            {req.price != null && (
                                                <div className="text-xl font-bold text-[#D4AF37]" data-testid={`price-${req.id}`}>${req.price.toFixed(2)}</div>
                                            )}
                                            {req.status === 'priced' && (
                                                <Button onClick={() => handlePayment(req)} className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold" data-testid={`pay-btn-${req.id}`}>
                                                    <CreditCard className="w-4 h-4 mr-2" /> Pay Now
                                                </Button>
                                            )}
                                            {req.status === 'paid' && (
                                                <p className="text-xs text-emerald-400">Production: 3-6 weeks</p>
                                            )}
                                            {req.status === 'completed' && (
                                                <p className="text-xs text-green-400">Content assigned to your account</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="bg-[#12151C] border border-white/5 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-white/70 mb-2">How Branded Content Works</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                        {[
                            { step: '1', label: 'Submit Request', desc: 'Describe your content needs' },
                            { step: '2', label: 'Get Quote', desc: 'Admin reviews & sets price' },
                            { step: '3', label: 'Pay & Wait', desc: 'One-time payment, 3-6 weeks production' },
                            { step: '4', label: 'Go Live', desc: 'Content assigned with EN, FR, ES, DE' },
                        ].map(s => (
                            <div key={s.step} className="space-y-1">
                                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] font-bold text-sm flex items-center justify-center mx-auto">{s.step}</div>
                                <p className="text-sm font-medium text-white/80">{s.label}</p>
                                <p className="text-xs text-white/40">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}
